type AreaReference = { modelID: number; colorID: number; sideID: number; areaID: number };
type SavedItem = { itemID: number; itemGuid?: string | null; areas?: ReadonlyArray<AreaReference | null> | null };
type SavedDesign = {
    designID: number;
    docID?: string | null;
    currentModificationID?: string | null;
    designItems?: ReadonlyArray<SavedItem | null> | null;
    sides?: ReadonlyArray<{ modelID: number; colorID: number; sideID: number } | null> | null;
};

const sideKey = (area: { modelID: number; colorID: number; sideID: number }) => `${area.modelID}:${area.colorID}:${area.sideID}`;
const areaKey = (area: AreaReference) => `${sideKey(area)}:${area.areaID}`;

/** Keep the live draft, but adopt assigned IDs before another save can create the same items again. */
export const reconcileComposerDesign = (design: MPlaza.Design, saved: SavedDesign) => {
    design.set('designID', saved.designID);
    (design as any).set('docID', saved.docID);
    (design as any).set('currentModificationID', saved.currentModificationID);
    if ((design as any).get('isDeleted')) return;
    const savedItems = new Map((saved.designItems ?? []).filter((item): item is SavedItem => !!item).map((item) => [item.itemGuid, item]));
    const missing: string[] = [];
    design.get('designItems').forEach((item) => {
        const persisted = savedItems.get(item.get('itemGuid'));
        if (!persisted) {
            missing.push(item.get('name') || item.get('itemGuid') || 'Unknown item');
            return;
        }
        item.set('itemID', persisted.itemID);
        const persistedAreas = new Set((persisted.areas ?? []).filter((area): area is AreaReference => !!area).map(areaKey));
        item.get('areas').forEach((area) => {
            if (!persistedAreas.has(areaKey(area.toJSON()))) missing.push(`item ${persisted.itemID}, area ${area.get('sideID')}`);
        });
    });
    const savedSides = new Set((saved.sides ?? []).filter((side) => !!side).map((side) => sideKey(side!)));
    design.get('sides').forEach((side) => {
        if (!savedSides.has(sideKey(side.toJSON()))) missing.push(`side ${side.get('sideID')}`);
    });
    if (missing.length) {
        // DesignService previously accepted missing product sides and silently discarded links.
        // Never clear the local draft or report a successful save in that situation.
        throw new Error(`The server did not preserve the template's area associations (${missing.join(', ')}). Your local draft is still available.`);
    }
};

export const composerDesignFingerprint = (design: MPlaza.Design) =>
    JSON.stringify(design, (key, value) => (key === 'tempPreviewImageData' || key === 'tempPreviewImageUrl' ? undefined : value));
